import React from 'react';
import { generateId } from '../../../utils/generateId';
import { Box, TextField, IconButton, Button } from '@mui/material';
import {
  Close as RemoveIcon,
  Add as AddIcon,
  ArrowUpward as UpIcon,
  ArrowDownward as DownIcon,
  CheckBoxOutlineBlank as CheckboxIcon,
} from '@mui/icons-material';
import { ContentBlock, ChecklistItem } from '../../../types/blocks';

interface ChecklistBlockEditorProps {
  block: ContentBlock;
  onChange: (block: ContentBlock) => void;
}

const ChecklistBlockEditor: React.FC<ChecklistBlockEditorProps> = ({ block, onChange }) => {
  const items: ChecklistItem[] = block.meta?.items || [];

  const updateItems = (newItems: ChecklistItem[]) => {
    onChange({ ...block, meta: { ...block.meta, items: newItems } });
  };

  const addItem = () => {
    updateItems([...items, { id: generateId(), text: '' }]);
  };

  const removeItem = (id: string) => {
    updateItems(items.filter((item) => item.id !== id));
  };

  const updateItemText = (id: string, text: string) => {
    updateItems(items.map((item) => (item.id === id ? { ...item, text } : item)));
  };

  const moveItem = (index: number, direction: 'up' | 'down') => {
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= items.length) return;
    const newItems = [...items];
    [newItems[index], newItems[swapIndex]] = [newItems[swapIndex], newItems[index]];
    updateItems(newItems);
  };

  return (
    <Box>
      {items.map((item, index) => (
        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.75 }}>
          <CheckboxIcon sx={{ fontSize: 20, color: 'text.disabled', flexShrink: 0 }} />
          <TextField
            fullWidth
            size="small"
            placeholder={`Task ${index + 1}`}
            value={item.text}
            onChange={(e) => updateItemText(item.id, e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.8125rem' } }}
          />
          <IconButton size="small" disabled={index === 0} onClick={() => moveItem(index, 'up')}>
            <UpIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" disabled={index === items.length - 1} onClick={() => moveItem(index, 'down')}>
            <DownIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <IconButton size="small" onClick={() => removeItem(item.id)} sx={{ color: 'error.main' }}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      ))}
      <Button
        size="small"
        startIcon={<AddIcon />}
        onClick={addItem}
        sx={{ mt: 0.5, textTransform: 'none', fontSize: '0.75rem' }}
      >
        Add item
      </Button>
    </Box>
  );
};

export default ChecklistBlockEditor;
